import type { FC, Ref, MutableRefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { pin3x, geoLacationNewDis } from '@images';
import {
  Map,
  Placemark,
  YMaps,
  Clusterer,
  ZoomControl
} from 'react-yandex-maps';
import { ClientOnly } from '@tanstack/react-router';
import { observer } from 'mobx-react';
import { useStores } from '@root/src/stores';
import { useRegionAwareCenter } from '@customHooks/useRegionAwareCenter';
import {
  YANDEX_MAP_VERSION,
  YANDEX_MAP_LANG,
  DEFAULT_MAP_ZOOM,
  REGION_OVERVIEW_MAP_ZOOM,
  ADDRESS_MAP_MODULES,
  CLUSTERER_MAP_MODULE,
  MAP_CLUSTERER_OPTIONS,
  MAP_ZOOM_CONTROL_OPTIONS
} from '@constants/map';
import { DEFAULT_MAP_CENTER } from '@constants/geo';

const isValidMapCenter = (value?: number[] | null): value is number[] =>
  Array.isArray(value) &&
  value.length >= 2 &&
  value.every(coord => Number.isFinite(coord));

interface CustomPlaceMarkProps {
  editAddress?: boolean;
  iconContentTemplate: string | null | undefined;
  isShowMapMarkPlace?: boolean;
  isDefaultMark: () => boolean;
  coords: number[];
}

const CustomPlaceMark: FC<CustomPlaceMarkProps> = observer(
  ({
    editAddress,
    iconContentTemplate,
    isShowMapMarkPlace,
    isDefaultMark,
    coords
  }) => {
    if (
      editAddress &&
      iconContentTemplate &&
      isShowMapMarkPlace &&
      !isDefaultMark()
    ) {
      return (
        <Placemark
          geometry={coords}
          options={{
            iconLayout: 'default#image',
            iconImageSize: [36, 36],
            iconImageOffset: [-18, -36],
            iconImageHref: geoLacationNewDis,
            iconContentLayout: iconContentTemplate
          }}
        />
      );
    } else {
      return null;
    }
  }
);

interface CustomPlaceMarkWithContentProps {
  iconContentTemplate: string | null | undefined;
  item: any;
  selectedAddress: number | string | null;
  onClick: (item: any) => void;
}

const CustomPlaceMarkWithContent: FC<CustomPlaceMarkWithContentProps> =
  observer(({ iconContentTemplate, item, selectedAddress, onClick }) => {
    const selectAddress = useCallback(() => {
      onClick(item);
    }, [item, onClick]);
    const isActiveAddress = item.id === Number(selectedAddress);
    const img = isActiveAddress ? geoLacationNewDis : pin3x;
    let coords: number[] | null = null;
    if (item.coords) {
      coords = item.coords.split(',').reverse().map(Number);
    } else if (item.dadata?.geoLat != null && item.dadata?.geoLon != null) {
      coords = [Number(item.dadata.geoLat), Number(item.dadata.geoLon)];
    }

    if (
      !coords ||
      coords.length < 2 ||
      !coords.every((coord: number) => Number.isFinite(coord))
    ) {
      return null;
    }

    const options = {
      iconLayout: 'default#image',
      iconImageSize: isActiveAddress ? [36, 36] : [18, 24],
      iconImageOffset: isActiveAddress ? [-18, -36] : [-9, -24],
      iconImageHref: img,
      iconContentLayout: iconContentTemplate
    };

    return (
      <Placemark
        key={item.id}
        geometry={coords}
        options={options}
        onClick={selectAddress}
      />
    );
  });

interface OrderRegisterCustomMapProps {
  mapRef: ((instance: Ref<any>) => void) | MutableRefObject<any> | undefined;
  onMapClick?: (e: any, mapRefName: string) => void;
  onLoadMap?: (ymaps: any) => void;
  coords?: number[];
  editAddress?: boolean;
  iconContentTemplate: string | null | undefined;
  isShowMapMarkPlace?: boolean;
  isDefaultMark?: () => boolean;
  height: string;
  width: string;
  selectedAddress?: number | string | null;
  selectAddress?: (item: any) => void;
  mapContainerStyle?: React.CSSProperties;
  isShowClusterer?: boolean;
  mapRefName?: string;
}

const OrderRegisterCustomMap: FC<OrderRegisterCustomMapProps> = observer(
  ({
    mapRef,
    onMapClick,
    onLoadMap,
    coords = [],
    editAddress,
    iconContentTemplate,
    isShowMapMarkPlace,
    isDefaultMark,
    height,
    width,
    selectedAddress = null,
    selectAddress = () => {},
    mapContainerStyle = {},
    isShowClusterer = false,
    mapRefName = 'mapRef'
  }) => {
    const { configStore, userStore, cartStore } = useStores();
    const { moscowAddresses } = userStore;
    const apikey = configStore.getValue('api_yandex_key');
    const [isMapReady, setIsMapReady] = useState(false);

    const viewportCenter = useRegionAwareCenter(
      isValidMapCenter(coords) ? coords : undefined
    );
    const mapCenter = isValidMapCenter(viewportCenter)
      ? viewportCenter
      : isValidMapCenter(coords)
      ? coords
      : DEFAULT_MAP_CENTER;

    const regionCenter = cartStore?.regionData?.fields?.geo_center;
    const isOverviewCenter =
      (isValidMapCenter(regionCenter) &&
        mapCenter[0] === regionCenter[0] &&
        mapCenter[1] === regionCenter[1]) ||
      (mapCenter[0] === DEFAULT_MAP_CENTER[0] &&
        mapCenter[1] === DEFAULT_MAP_CENTER[1]);

    const mapModules = useMemo(
      () =>
        isShowClusterer
          ? [...ADDRESS_MAP_MODULES, CLUSTERER_MAP_MODULE]
          : ADDRESS_MAP_MODULES,
      [isShowClusterer]
    );

    const instanceRef: ((instance: Ref<any>) => void) | undefined =
      mapRef == null
        ? undefined
        : typeof mapRef === 'function'
        ? mapRef
        : (instance: Ref<any>) => {
            mapRef.current = instance;
          };

    const handleLoad = useCallback(
      (ymaps: any) => {
        onLoadMap?.(ymaps);
        setIsMapReady(true);
      },
      [onLoadMap]
    );

    // react-yandex-maps по умолчанию глотает ошибки инициализации (onError =
    // noop) и при сбое просто не рендерит карту. Логируем, чтобы пустой
    // div.smart-map не приходилось отлаживать вслепую.
    const handleMapError = useCallback((error: unknown) => {
      // eslint-disable-next-line no-console
      console.error('[OrderRegisterCustomMap] ymaps init failed:', error);
    }, []);

    const addressMarks =
      !!moscowAddresses?.length &&
      moscowAddresses.map(item => (
        <CustomPlaceMarkWithContent
          key={item.id}
          iconContentTemplate={iconContentTemplate}
          item={item}
          selectedAddress={selectedAddress}
          onClick={selectAddress}
        />
      ));
    const hasAddressMarks =
      Array.isArray(addressMarks) && addressMarks.length > 0;

    return (
      <div className='smart-map' style={mapContainerStyle}>
        {apikey && (
          <ClientOnly fallback={null}>
<YMaps
            version={YANDEX_MAP_VERSION}
            query={{
              lang: YANDEX_MAP_LANG,
              apikey
            }}
          >
            <Map
              instanceRef={instanceRef}
              onLoad={handleLoad}
              onError={handleMapError}
              state={{
                center: mapCenter,
                zoom: isOverviewCenter
                  ? REGION_OVERVIEW_MAP_ZOOM
                  : DEFAULT_MAP_ZOOM
              }}
              height={height}
              width={width}
              modules={mapModules}
              onClick={e => {
                onMapClick?.(e, mapRefName);
              }}
            >
              <ZoomControl options={MAP_ZOOM_CONTROL_OPTIONS} />

              {isMapReady && (
                <>
                  <CustomPlaceMark
                    editAddress={editAddress}
                    iconContentTemplate={iconContentTemplate}
                    isShowMapMarkPlace={isShowMapMarkPlace}
                    isDefaultMark={isDefaultMark ?? (() => false)}
                    coords={coords}
                  />

                  {isShowClusterer && hasAddressMarks ? (
                    <Clusterer options={MAP_CLUSTERER_OPTIONS}>
                      {addressMarks}
                    </Clusterer>
                  ) : (
                    <>
                      {!editAddress &&
                        hasAddressMarks &&
                        iconContentTemplate &&
                        addressMarks}
                    </>
                  )}
                </>
              )}
            </Map>
          </YMaps>
</ClientOnly>
        )}
      </div>
    );
  }
);

export default OrderRegisterCustomMap;
